import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons/faAngleDown';
import { faAngleUp } from '@fortawesome/pro-light-svg-icons/faAngleUp';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { PageList } from 'src/modules/contentList/components/moduleCard/pageList';
import { MODULE_DRAG_NODE_PROPS } from 'src/modules/contentList/hooks/useModuleDragAndDrop';
import { useOpenParentContainerOnDragOver } from 'src/modules/contentList/hooks/useOpenParentContainerOnDragOver';
import { usePageDragAndDrop } from 'src/modules/contentList/hooks/usePageDragAndDrop';
import { useScrollToHighlightedModule } from 'src/modules/contentList/hooks/useScrollToHighlightedModule';
import { contentListQueries } from 'src/modules/contentList/queries';
import { Icon } from 'src/shared/components/icon';
import { HtmlHelper } from 'src/shared/helpers/html';
import Content from './content';
import Header from './header';
const ModuleCard = ({ active = false, module, isDragging = false }) => {
    const ref = useRef(null);
    const [opened, setOpened] = useState(active);
    const isOpen = opened && !isDragging;
    const pages = useQuery({
        ...contentListQueries.getPages(module.id),
        enabled: isOpen
    });
    const { loaded, parentProps, pagesCopy, isDraggingPage } = usePageDragAndDrop({
        pages: pages.data,
        moduleId: module.id
    });
    const { parentContainerProps, isOver } = useOpenParentContainerOnDragOver({ setOpened });
    useScrollToHighlightedModule({
        moduleId: module.id,
        onHighlight: useCallback(() => {
            setOpened(true);
            ref.current?.scrollIntoView();
            return Promise.resolve();
        }, [])
    });
    // Check if the click occurred inside the ModuleCard, but outside the buttons
    function handleCardClick(event) {
        if (!HtmlHelper.isInteractive(event.target)) {
            setOpened((prev) => !prev);
        }
    }
    return (_jsxs("li", { className: clsx('flex flex-col rounded-extralarge border border-neutral-200 bg-neutral-0 hover:bg-neutral-0', {
            'opacity-40': isDragging,
            'outline outline-2 outline-primary-300': isOver && isDraggingPage
        }), "aria-labelledby": `module-heading-${module.id}`, id: `module-${module.id}`, ...parentContainerProps, ...MODULE_DRAG_NODE_PROPS, ref: ref, children: [_jsx(Header, { module: module, isOpen: isOpen, onClick: handleCardClick }), _jsx(Content, { isOpen: isOpen, moduleId: module.id, children: _jsxs("ul", { className: "relative mt-[-1rem] flex flex-col gap-2", ...parentProps, children: [_jsx("div", { className: "h-4" }), _jsx(PageList, { status: loaded ? pages.status : 'pending', onTryAgain: pages.refetch, data: pagesCopy, totalPages: module.totalPages })] }) }), _jsx("div", { className: "mt-1 flex items-center justify-center border-t-small border-neutral-200 p-3 md:hidden", onClick: handleCardClick, children: _jsx(Icon, { icon: isOpen ? faAngleUp : faAngleDown }) })] }));
};
export default ModuleCard;
